import styled from '@emotion/styled';
import { CommentIndexWrapper } from '@seedlang/hoc';
import { AppUI } from '@seedlang/state';
import { isPresent } from '@seedlang/utils';
import autobind from 'autobind-decorator';
import CommentCreate from 'components/comment/comment_create';
import CommentShow from 'components/comment/comment_show';
import InPlaceSelect from 'components/form/in_place_select';
import ForumThreadPost from 'components/forum_thread/forum_thread_post';
import Spinner from 'components/spinner';
import Text from 'components/text';
import NoAccessButton from 'components/worksheet/no_access_button';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

const Wrapper = styled.div`
  margin: ${props => props.margin};
  font-size: 12px;
  width: 100%;
  background: white;
  border-radius: ${props => props.borderRadius};
  padding: ${props => props.padding};
  select {
    height: 30px;
  }
`;

const CommentsRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
  padding-top: 10px;
  border-top: ${props => props.borderTop};
`;

const SelectWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  align-items: center;
`;

const CommentCreateWrapper = styled.div`
  position: relative;
  pointer-events: ${props => props.hasNoAccess ? 'none' : 'auto'};
`;

const CommentCreateOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.8));
  pointer-events: none;
  z-index: 1;
`;

const NoAccessButtonWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  pointer-events: auto;
  background: white;
`;

const ScrollButton = styled.button`
  height: 35px;
  padding: 0 12px;
  background: #fff;
  border: 1px solid #dedede;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  &:hover {
    background: #e0e0e0;
  }
`;

@observer
class CommentIndex extends React.Component {
  @observable selectedCommentId = null;

  constructor(props) {
    super(props);
    this.selectedCommentId = isPresent(window.location.hash) ? window.location.hash.replace('#comment-', '') : null;
  }

  static defaultProps = {
    margin: '0 0 20px 0',
    padding: '20px',
    borderRadius: '5px',
  };

  @autobind scrollToLatestComment() {
    if (isPresent(this.props.latestCommentId)) {
      this.selectedCommentId = this.props.latestCommentId;
      const element = document.getElementById(`comment-${this.props.latestCommentId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }

  render() {
    return (
      <Wrapper
        margin={this.props.margin}
        padding={this.props.padding}
        borderRadius={this.props.borderRadius}
      >
        {
          this.props.showForumThreadPost &&
            <ForumThreadPost
              hideUserProfile={this.props.hideUserProfile}
              groupId={this.props.groupId}
              inModal={this.props.inModal}
              post={this.props.post}
              user={AppUI.user}
              onChange={this.props.onChange}
              onUpdateForumThread={this.props.onUpdateForumThread}
              hideForumThreadDetails={this.props.hideForumThreadDetails}
              languageId={this.props.languageId || AppUI.targetLanguageId}
            />
        }
        {
          (this.props.showCommentCreate || this.props.showNoAccessButton) &&
            <CommentCreateWrapper hasNoAccess={this.props.showNoAccessButton}>
              <CommentCreate
                debugInfo={this.props.debugInfo}
                showXp={this.props.showXp}
                blockTextExpansion={this.props.blockTextExpansion}
                wordId={this.props.wordId}
                sentenceId={this.props.sentenceId}
                conceptId={this.props.conceptId}
                namespace={this.props.namespace || this.props.translationId || this.props.conceptId}
                commentable={this.props.commentable}
                commentableType={this.props.commentableType}
                commentableId={this.props.commentableId}
                afterCreateComment={this.props.onChange}
                languageId={this.props.languageId || AppUI.targetLanguageId}
                placeholder={this.props.placeholder}
                margin='0 0 10px 0'
              />
              {
                this.props.showNoAccessButton &&
                  <>
                    <CommentCreateOverlay />
                    <NoAccessButtonWrapper>
                      <NoAccessButton margin='0' />
                    </NoAccessButtonWrapper>
                  </>
              }
            </CommentCreateWrapper>
        }
        <span id='page-top' />
        {
          this.props.showSpinner &&
            <Spinner />
        }
        {
          this.props.comments.length > 1 &&
            <CommentsRow
              borderTop={this.props.showCommentCreate ? '1px solid #dedede' : ''}
            >
              {
                this.props.showCommentCount &&
                  <Text
                    bold
                  >
                    {AppUI.user.commentOrdering === 'created_at' ? 'Oldest' : 'Newest'} Comments
                  </Text>
              }
              <SelectWrapper>
                {
                  this.props.comments.length > 5 &&
                    <ScrollButton onClick={this.scrollToLatestComment}>
                      Go to Newest
                    </ScrollButton>
                }
                <InPlaceSelect
                  allowUpdate
                  value={AppUI.user.commentOrdering}
                  onChange={this.props.setCommentOrdering}
                  options={[
                    ['created_at', 'Sort by Old'],
                    ['-created_at', 'Sort by New'],
                  ]}
                />
              </SelectWrapper>
            </CommentsRow>
        }
        {
          this.props.comments.map(item => {
            if (!item.hidden ||
                item.user.id === AppUI.user.id ||
                (item.respondToId === AppUI.user.id && item.user.anyAdminOrModerator) ||
                AppUI.user.admin ||
                AppUI.user.moderator) {
              return (
                <CommentShow
                  key={item.id}
                  hideUserProfile={this.props.hideUserProfile}
                  indentation={this.props.indentation}
                  onChange={this.props.onChange}
                  commentable={this.props.commentable}
                  commentableType={this.props.commentableType}
                  commentableId={this.props.commentableId}
                  comment={item}
                  user={this.props.user}
                  fontSize={this.props.fontSize}
                  userProfileImageSize={this.props.userProfileImageSize}
                  margin={this.props.commentMargin}
                  padding={this.props.padding}
                  languageId={this.props.languageId || AppUI.targetLanguageId}
                  selected={this.selectedCommentId === item.id}
                />
              );
            } else {
              return null;
            }
          })
        }
      </Wrapper>
    );
  }
}

export default CommentIndexWrapper(CommentIndex);
